import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Award, Clock, LogOut, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

export default function AdminDashboard() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [participants, setParticipants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pRes, sRes] = await Promise.all([api.get("/admin/participants"), api.get("/admin/stats")]);
      setParticipants(pRes.data);
      setStats(sRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const statCards = stats
    ? [
        { key: "total_participants", label: "Total Participants", value: stats.total_participants, icon: Users },
        { key: "total_certified", label: "Certified", value: stats.total_certified, icon: Award },
        { key: "total_attempts", label: "Quiz Attempts", value: stats.total_attempts, icon: Clock },
      ]
    : [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 sm:px-8 h-16 flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-slate-900">DENSO Training Admin</h1>
          <p className="text-xs text-slate-500">Signed in as {admin?.email}</p>
        </div>
        <Button variant="outline" onClick={handleLogout} data-testid={ADMIN.logoutButton}>
          <LogOut className="h-4 w-4" /> Logout
        </Button>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 space-y-8">
        <div className="grid sm:grid-cols-3 gap-4">
          {statCards.map((s) => (
            <div key={s.key} data-testid={ADMIN.statCard(s.key)} className="bg-white rounded-md border border-slate-200 p-5 flex items-center gap-4">
              <div className="h-10 w-10 rounded-md bg-red-50 flex items-center justify-center">
                <s.icon className="h-5 w-5 text-denso-red" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Participants</h2>
            <p className="text-xs text-slate-500">Attempts, time on quiz and completion status per participant</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Province</TableHead>
                <TableHead>Attempts (cycle)</TableHead>
                <TableHead>Latest Score</TableHead>
                <TableHead>Latest Time</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-400 py-8">Loading...</TableCell>
                </TableRow>
              )}
              {!loading && participants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-400 py-8">No participants yet.</TableCell>
                </TableRow>
              )}
              {participants.map((p) => (
                <TableRow key={p.id} data-testid={ADMIN.participantRow(p.id)}>
                  <TableCell className="font-medium">{p.name} {p.surname}</TableCell>
                  <TableCell>{p.branch}</TableCell>
                  <TableCell>{p.province}</TableCell>
                  <TableCell>{p.attempts_used_in_cycle}/4 (cycle {p.cycle_number})</TableCell>
                  <TableCell>{p.latest_score_percentage != null ? `${p.latest_score_percentage}%` : "—"}</TableCell>
                  <TableCell>{p.latest_time_taken_seconds != null ? `${p.latest_time_taken_seconds}s` : "—"}</TableCell>
                  <TableCell>
                    <Badge className={p.certified ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}>
                      {p.certified ? "Certified" : "In Progress"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" data-testid={ADMIN.viewAttemptsButton(p.id)} onClick={() => navigate(`/admin/participants/${p.id}`)}>
                      <Eye className="h-3.5 w-3.5" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
